import { useCallback, useRef } from 'react'
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { Button } from '@/src/components/ui/Button'
import { KeyboardAwareScrollLayout } from '@/src/components/ui/KeyboardAwareScrollLayout'
import { colors } from '@/src/theme'

import { PostPaymentStepProgress } from '../../components/PostPaymentStepProgress'
import {
  CONTROL_BORDER_RADIUS,
  getSelectableBorderColor,
  NON_SCALING_TEXT_PROPS,
  SECTION_LABEL_STYLE,
  SELECTABLE_CONTROL_HEIGHT,
  SELECTABLE_TEXT_STYLE,
} from './constants'
import { usePostPaymentOnboardingContext } from './PostPaymentOnboardingContext'
import { PostPaymentOnboardingScreenHeader } from './PostPaymentOnboardingScreenHeader'
import { DAY_OPTIONS } from './state'

export function PostPaymentOnboardingDetailsStep() {
  const scrollViewRef = useRef<ScrollView>(null)
  const {
    state,
    dispatch,
    isSubmitting,
    canContinueDetails,
    detailsFooterPaddingBottom,
    exerciseFrequencyLabel,
    knowledgeLevelLabel,
    knowledgeLevelOptions,
    logoutLabel,
    notesLabel,
    notesPlaceholder,
    voiceTypeLabel,
    voiceTypeOptions,
    nextLabel,
    loadingLabel,
    postPaymentProgressTotal,
    handleSignOut,
    handleComplete,
  } = usePostPaymentOnboardingContext()

  const handleNotesFocus = useCallback(() => {
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true })
    })
  }, [])

  return (
    <SafeAreaView
      className="bg-background-primary"
      style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}
    >
      <StatusBar style="dark" />
      <View className="px-6 pt-6 pb-4">
        <PostPaymentOnboardingScreenHeader
          isSigningOut={state.isSigningOut}
          onSignOut={handleSignOut}
          logoutLabel={logoutLabel}
        />
        <PostPaymentStepProgress
          step="details"
          totalSteps={postPaymentProgressTotal}
        />
      </View>
      <KeyboardAwareScrollLayout
        scrollViewRef={scrollViewRef}
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 8,
        }}
        footerStyle={{
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: detailsFooterPaddingBottom,
        }}
        footer={
          <Button
            label={isSubmitting ? loadingLabel : nextLabel}
            onPress={() => {
              if (!canContinueDetails) {
                return
              }

              void handleComplete()
            }}
            variant="primary"
            size="md"
            loading={isSubmitting}
            disabled={!canContinueDetails || isSubmitting}
            containerClassName="w-full"
          />
        }
      >
        <Text
          {...NON_SCALING_TEXT_PROPS}
          className="mb-3"
          style={SECTION_LABEL_STYLE}
        >
          {knowledgeLevelLabel}
        </Text>
        <View className="mb-8 flex-row gap-2">
          {knowledgeLevelOptions.map((knowledgeLevel) => {
            const isSelected =
              state.selectedKnowledgeLevel === knowledgeLevel.key
            return (
              <Pressable
                key={knowledgeLevel.key}
                onPress={() =>
                  dispatch({
                    type: 'setSelectedKnowledgeLevel',
                    payload: knowledgeLevel.key,
                  })
                }
                className={`flex-1 rounded-[8px] border px-2 items-center justify-center ${isSelected ? 'bg-secondary700' : 'bg-background-secondary'}`}
                style={{
                  height: SELECTABLE_CONTROL_HEIGHT,
                  borderRadius: CONTROL_BORDER_RADIUS,
                  borderColor: getSelectableBorderColor(isSelected),
                }}
              >
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={SELECTABLE_TEXT_STYLE}
                >
                  {knowledgeLevel.label}
                </Text>
              </Pressable>
            )
          })}
        </View>

        <Text
          {...NON_SCALING_TEXT_PROPS}
          className="mb-3"
          style={SECTION_LABEL_STYLE}
        >
          {exerciseFrequencyLabel}
        </Text>
        <View
          className="mb-8"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {DAY_OPTIONS.map((dayCount) => {
            const isSelected = state.selectedDaysPerWeek === dayCount
            return (
              <Pressable
                key={dayCount}
                onPress={() =>
                  dispatch({
                    type: 'setSelectedDaysPerWeek',
                    payload: dayCount,
                  })
                }
                className={`border items-center justify-center ${isSelected ? 'bg-secondary700' : 'bg-background-secondary'}`}
                style={{
                  width: SELECTABLE_CONTROL_HEIGHT,
                  height: SELECTABLE_CONTROL_HEIGHT,
                  borderRadius: CONTROL_BORDER_RADIUS,
                  borderColor: getSelectableBorderColor(isSelected),
                }}
              >
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  numberOfLines={1}
                  style={SELECTABLE_TEXT_STYLE}
                >
                  {dayCount}
                </Text>
              </Pressable>
            )
          })}
        </View>

        <Text
          {...NON_SCALING_TEXT_PROPS}
          className="mb-3"
          style={SECTION_LABEL_STYLE}
        >
          {voiceTypeLabel}
        </Text>
        <View className="mb-8 gap-2">
          {voiceTypeOptions.map((voiceType) => {
            const isSelected = state.selectedVoiceType === voiceType.key
            return (
              <Pressable
                key={voiceType.key}
                onPress={() =>
                  dispatch({
                    type: 'setSelectedVoiceType',
                    payload: voiceType.key,
                  })
                }
                className={`rounded-[8px] border px-5 justify-center ${isSelected ? 'bg-secondary700' : 'bg-background-secondary'}`}
                style={{
                  height: SELECTABLE_CONTROL_HEIGHT + 8,
                  borderRadius: CONTROL_BORDER_RADIUS,
                  borderColor: getSelectableBorderColor(isSelected),
                }}
              >
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  numberOfLines={1}
                  style={SELECTABLE_TEXT_STYLE}
                >
                  {voiceType.label}
                </Text>
              </Pressable>
            )
          })}
        </View>

        <Text
          {...NON_SCALING_TEXT_PROPS}
          className="mb-3"
          style={SECTION_LABEL_STYLE}
        >
          {notesLabel}
        </Text>
        <TextInput
          value={state.additionalNotes}
          onChangeText={(value) =>
            dispatch({ type: 'setAdditionalNotes', payload: value })
          }
          onFocus={handleNotesFocus}
          multiline
          textAlignVertical="top"
          className="h-[160px] rounded-[8px] border bg-background-secondary px-5 py-4 text-ink text-base mb-6"
          style={{
            borderRadius: CONTROL_BORDER_RADIUS,
            borderColor: colors.grayMid,
          }}
          placeholder={notesPlaceholder}
          placeholderTextColor={colors.muted}
        />
      </KeyboardAwareScrollLayout>
    </SafeAreaView>
  )
}
